// Angular Imports
import { Location as RouteLocation } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';

import { env } from '@amfam/environment';
// lodash

// My Account Imports
import { Address, Policy, PolicyAnalytics, RiskModel } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { AgentInfo, Driver, PolicyChangeRequestType } from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { PolicyChangeRequestSendAnalyticAction } from '../../+state/policy-change.actions';
import {
  AddressChange,
  ChangeRequest,
  ChangeRequestPolicies,
  DriverLicenseChange,
  RequestChange
} from '../../models';
import { PolicyTransmuteService } from '../../services/policy-transmute.service';

@Component({
  selector: 'app-plchangerequest-form',
  templateUrl: './change-request-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChangeRequestFormComponent implements OnInit {
  // Inputs which are needed by the sub components
  @Input() firstName: string;
  @Input() lastName: string;
  @Input() currentAddress: Address;
  @Input() policyList: Policy[];
  @Input() propertyPolicies: Policy[];
  @Input() agents: AgentInfo[];
  @Input() addressInfo: Partial<AddressChange>;
  @Input() driverLicense: Partial<DriverLicenseChange>;
  @Input() drivers: Driver[];
  @Input() vinInfo: Partial<RequestChange>;
  @Input() risks: RiskModel[];
  @Input() newTemplate = false;
  @Input() years: string[];
  @Input() isPartner: boolean;

  // Emit the events to parent coming from the child components.
  @Output() changeRequest = new EventEmitter<ChangeRequest>();
  @Output() handleCancel = new EventEmitter();

  requestChangeTypeValues: { [id: string]: number };
  selectedMethodType: number;
  modalId = 'saveChanges';
  valueChanged: boolean;

  public policies: ChangeRequestPolicies;
  public amfamDotComUrl: string;
  public changeRequestSelectedType = PolicyChangeRequestType;
  public PolicyChangeRequestType = PolicyChangeRequestType;

  public changeRequestForm: UntypedFormGroup;
  public addressGroup: UntypedFormGroup;
  public emailInput: AbstractControl;
  public phoneInput: AbstractControl;
  public typeInput: AbstractControl;
  public tpiRequestChangeEnabled = false;
  public policyChangeTypeLabel: string;
  addressChangeAnalytics = this.copyService.getCopy('policy.addressChange.analytics');
  private selectedPolicyNumber: string;

  constructor(
    private copyService: CopyService,
    private formBuilder: UntypedFormBuilder,
    private modalService: DsModalService,
    public roleService: ImpersonateRolesService,
    private store: Store<fromRoot.RootState>,
    private policyTransmuteService: PolicyTransmuteService,
    private location: RouteLocation,
    private featureFlagService: FeatureFlagService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit() {
    this.valueChanged = false;

    this.policyChangeTypeLabel = this.copyService.get(
      'policy',
      'policyChangeTypeSelectionTypeLabel'
    );

    this.tpiRequestChangeEnabled = this.featureFlagService.isEnabled('tpi_request_change');
    // Build the change request form
    this.changeRequestForm = this.buildChangeRequestForm(this.formBuilder);

    this.amfamDotComUrl = env.links.amfamDotComUrl;

    // Initialize the form controls
    this.typeInput = this.policyTransmuteService.initializeFormControls(this.changeRequestForm)[0];

    this.selectedMethodType = (this.location.getState() as any)?.changeRequestType || '';
    this.selectedPolicyNumber =
      (this.location.getState() as any)?.selectedPolicyNumber ||
      (this.propertyPolicies && this.propertyPolicies[0]?.policyNumber) ||
      '';
    this.changeRequestForm.patchValue({
      typeInput: this.selectedMethodType
    });

    if (this.selectedMethodType) this.onTypeChangeAction(this.selectedMethodType);

    // Get the copy details
    this.requestChangeTypeValues = this.copyService.get(
      'policy',
      this.newTemplate ? 'requestTypes' : 'requestType'
    );

    if (this.drivers?.length === 0) {
      this.requestChangeTypeValues = this.removeObjectByKey(
        PolicyChangeRequestType.DRIVER_LICENSE_CHANGE
      );
    }
    if (
      !this.tpiRequestChangeEnabled ||
      (this.requestChangeTypeValues && !(this.propertyPolicies && this.propertyPolicies.length > 0))
    ) {
      // Remove change Mortgage lender details option if there are no property policies
      this.requestChangeTypeValues = this.removeObjectByKey(
        PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE
      );
    }
  }

  private removeObjectByKey(key: number) {
    const removeTypePropKey = Object.keys(this.requestChangeTypeValues).find(
      k => this.requestChangeTypeValues[k] === key
    );
    const { [removeTypePropKey]: removedType, ...otherTypes } = this.requestChangeTypeValues;
    return otherTypes;
  }

  /**
   * @author: Abhishek Singh
   * @param void
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  private buildChangeRequestForm(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({
      typeInput: ['', Validators.compose([Validators.required])]
    });
  }

  /**
   * @author: Abhishek Singh
   * @param changeRequestPolicies : These are the selected policies passed from the change-request-policy component
   * @returns void
   */
  public handlePolicies(changeRequest: ChangeRequest): void {
    this.changeRequest.emit(changeRequest);
  }

  /**
   * @author: Abhishek Singh
   * @param changeRequestPolicies : These are the selected policies passed from the change-request-policy component
   * @returns void
   */
  public handleNameAddress(changeRequest: ChangeRequest): void {
    changeRequest = Object.assign({}, changeRequest, { policyList: this.policyList });
    this.changeRequest.emit(changeRequest);
  }

  public handleLicenseChange(changeRequest: ChangeRequest): void {
    this.changeRequest.emit(changeRequest);
  }

  public handleVinChange(changeRequest: ChangeRequest): void {
    this.changeRequest.emit(changeRequest);
  }

  /**
   * @param: changeRequestSelectedType : this value is passed to the modal change to get the type selected.
   * @returns: void
   * @description: Opens the modal with the alert to user about loosing the data.
   */

  public onTypeChangeAction(value): void {
    this.selectedMethodType = parseInt(value, 10);
    if (this.selectedMethodType === PolicyChangeRequestType.ADDRESS_CHANGE) {
      this.analyticsFacade.trackEvent({ event: this.addressChangeAnalytics.start.event });
    } else
      this.store.dispatch(
        PolicyChangeRequestSendAnalyticAction({
          payload: {
            pageAnalytic: PolicyAnalytics.policyChangeRequestPageAnalytic,
            changeTypeSelected: this.selectedMethodType
          }
        })
      );
  }

  /**
   * @author: Abhishek Singh
   * @param changeRequestSelectedType : This is the value which gets passed from the form for the type selected
   * @returns void
   * @description: Once the value is changed reset the commentInput and set the selectedMethodType
   */
  public onTypeChange(changeRequestSelectedType): void {
    if (!this.valueChanged) {
      this.onTypeChangeAction(changeRequestSelectedType);
    } else if (this.valueChanged) {
      this.modalService.open(this.modalId);
      this.changeRequestSelectedType = changeRequestSelectedType;
    }
  }

  /**
   * @param: changeRequestSelectedType : this value is passed to the modal change to get the type selected.
   * @returns: void
   * @description: Opens the modal with the alert to user about loosing the data.
   */
  public onChange(changeRequestSelectedType): void {
    this.onTypeChangeAction(changeRequestSelectedType);
    this.modalService.close(this.modalId);
    this.valueChanged = false;
  }

  /**
   * @param: None
   * @returns: void
   * @description: Closes the modal .
   */
  public closeModal(selectedMethodType: number) {
    this.modalService.close(this.modalId);
    this.typeInput.setValue(selectedMethodType.toString());
  }

  /**
   * @param: None
   * @returns: void
   * @description: emits when the valuechanges in the form.
   */
  public handlevalueChangeEvent() {
    this.valueChanged = true;
  }

  /**
   * @param: None
   * @returns: void
   * @description: routes to policies .
   */
  public handleCancelEvent(): void {
    this.handleCancel.emit();
  }

  /**
   * @params {string, string} a , b
   * @returns always return true to maintain the default order.
   */
  public sortFn = (a, b) => true;

  public handleMortgageLenderChanges(changeRequest: ChangeRequest): void {
    changeRequest = Object.assign({}, changeRequest, {
      policyList: this.propertyPolicies,
      selectedPolicyNumber: changeRequest?.selectedPolicyNumber || this.selectedPolicyNumber
    });
    this.changeRequest.emit(changeRequest);
  }
}
